import { resolveProposalCustomerPricingDisplay } from "./proposalCustomerPricing";

export type ManualApprovedRetailPricingJson = {
  listTotal?: number | null;
  originalListPrice?: number | null;
  specialDiscountSgd?: number | null;
  specialDiscountAmount?: number | null;
  agreedPriceSgd?: number | null;
  agreedPrice?: number | null;
  showDiscountPctToCustomer?: boolean | null;
  showDiscountPercentage?: boolean | null;
  discountPercentage?: number | null;
  pricesVisibleToCustomer?: boolean | null;
};

export type ManualApprovedCommercialSummary = {
  originalPrice: number;
  discountAmount: number;
  finalApprovedPrice: number;
  showDiscountPct: boolean;
  discountPct: number;
  hasDiscount: boolean;
  leadPriceLabel: "PROPOSAL TOTAL";
  showFinalAgreedPrice: boolean;
};

export type ManualApprovedPaymentTerm = {
  stage: string;
  percentage: number;
  fixedAmount?: number | null;
};

export function manualApprovedProposalWrapperPath(proposalId: number): string {
  return `/proposal-manual/${proposalId}`;
}

export function staffManualApprovedProposalPreviewPath(proposalId: number): string {
  return `/sales/proposals/${proposalId}/preview-manual`;
}

/** Staff Link A for customer-card timeline manual PDF upload milestones. */
export function staffManualUploadTimelineLinkA(proposal: {
  id: number;
  projectId?: number | null;
  originalProposalStorageKey?: string | null;
}): string {
  const key = String(proposal.originalProposalStorageKey ?? "").trim();
  if (key.length > 0) {
    const pid = proposal.projectId != null ? Number(proposal.projectId) : NaN;
    if (Number.isFinite(pid) && pid > 0) {
      return `/proposal-wrapper/${pid}/${proposal.id}`;
    }
    return staffManualApprovedProposalPreviewPath(proposal.id);
  }
  return `/sales/proposals/${proposal.id}/deal`;
}

export function manualApprovedProposalWrapperUrl(proposalId: number, baseUrl: string): string {
  const base = baseUrl.replace(/\/+$/, "");
  return `${base}${manualApprovedProposalWrapperPath(proposalId)}`;
}

function roundMoney(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

function moneyNumber(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

export function resolveManualApprovedCommercialSummary(input: {
  totalAmount?: unknown;
  retailPricingJson?: ManualApprovedRetailPricingJson | null;
}): ManualApprovedCommercialSummary {
  const retailPricing = input.retailPricingJson ?? null;
  const pricing = resolveProposalCustomerPricingDisplay({
    totalAmount: input.totalAmount,
    retailPricingJson: retailPricing,
  });
  const hasExplicitDiscount =
    retailPricing?.specialDiscountSgd != null || retailPricing?.specialDiscountAmount != null;
  const discountAmount = roundMoney(
    Math.max(
      0,
      hasExplicitDiscount
        ? moneyNumber(retailPricing?.specialDiscountSgd ?? retailPricing?.specialDiscountAmount)
        : pricing.proposalTotal - pricing.finalAgreedPrice
    )
  );
  const showDiscountPct = Boolean(retailPricing?.showDiscountPctToCustomer ?? retailPricing?.showDiscountPercentage);
  const discountPct = roundMoney(
    retailPricing?.discountPercentage != null
      ? moneyNumber(retailPricing.discountPercentage)
      : pricing.proposalTotal > 0
        ? (discountAmount / pricing.proposalTotal) * 100
        : 0
  );
  const hasDiscount = discountAmount > 0.004;
  return {
    originalPrice: pricing.proposalTotal,
    discountAmount,
    finalApprovedPrice: pricing.finalAgreedPrice,
    showDiscountPct,
    discountPct,
    hasDiscount,
    leadPriceLabel: "PROPOSAL TOTAL",
    showFinalAgreedPrice: pricing.showFinalAgreedPrice,
  };
}

export function resolveManualApprovedPaymentTerms(
  paymentTermsJson: unknown
): ManualApprovedPaymentTerm[] {
  if (!Array.isArray(paymentTermsJson)) return [];
  return paymentTermsJson
    .map((row) => {
      if (!row || typeof row !== "object") return null;
      const stage = String((row as { stage?: unknown }).stage ?? "").trim() || "Stage";
      const percentage = Number((row as { percentage?: unknown }).percentage);
      if (!Number.isFinite(percentage) || percentage <= 0) return null;
      return { stage, percentage };
    })
    .filter((row): row is ManualApprovedPaymentTerm => row != null);
}
